/**
 * GuideChat – Agnostic guide chat UI (messages, question cards, actions).
 * Used by CoPilotGuide for all guide flows; content is driven by config and widget registry.
 */

import { Button, Input } from '@tonnie/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, ChevronDown } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import type { IAction, IMessage } from './types';
import { BrainProcessingCard, type BrainProcessingCardProps } from './BrainProcessingCard';
import type { InstantiationState } from './CoPilotGuide';
import { ChatInput } from './ChatInput';
import { LoadingIndicator } from './LoadingIndicator';
import { MessageBubble } from './MessageBubble';
import { QuestionCard } from './QuestionCard';

export interface IGuideChatProps {
  messages: IMessage[];
  onSendMessage: (message: string) => void;
  onSelectOption?: (option: string | number) => void;
  isLoading?: boolean;
  currentQuestion?: IMessage;
  hideInput?: boolean;
  actions?: IAction[];
  onActionSelect?: (action: IAction) => void;
  isAnimating?: boolean;
  blendedWidget?: React.ReactNode;
  /**
   * Identity of the mounted blended widget (its chat id). The auto-scroll keys
   * on THIS, never on `blendedWidget` itself: the host builds a fresh element
   * on every render, so keying on the element re-ran the scroll on every
   * parent render and kept the chat — and any button in it — in motion.
   */
  blendedWidgetKey?: string | null;
  instantiationState?: InstantiationState | null;
  onInstantiationContinue?: () => void;
  onInstantiationRetry?: () => void;
}

export function GuideChat({
  messages,
  onSendMessage,
  onSelectOption,
  isLoading = false,
  currentQuestion,
  hideInput = false,
  actions = [],
  onActionSelect,
  isAnimating = false,
  blendedWidget,
  blendedWidgetKey = null,
  instantiationState,
  onInstantiationContinue,
  onInstantiationRetry,
}: IGuideChatProps) {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const SCROLL_THRESHOLD = 100;

  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setTimeout(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 50);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  // Follow the conversation when its CONTENT changes: a message added or
  // finished typing, the loading row, a widget mounted, the brain card changing
  // state. Every dep is a content signal — not an object rebuilt per render.
  const instantiationStatus = instantiationState?.status ?? null;
  useEffect(() => {
    if (isNearBottomRef.current) scrollToBottom();
  }, [messages, isLoading, blendedWidgetKey, instantiationStatus, scrollToBottom]);

  const handleSelectOption = (option: string | number) => {
    onSelectOption?.(option);
  };

  const [showScrollPill, setShowScrollPill] = useState(false);

  useEffect(() => {
    setShowScrollPill(!isNearBottomRef.current);
  }, [messages]);

  const handleScrollPillClick = useCallback(() => {
    isNearBottomRef.current = true;
    setShowScrollPill(false);
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden relative" data-testid="guide-chat">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3"
      >
        {/* Messages */}
        {messages.map((message) => (
          <div key={message.id} data-testid="guide-chat-message">
            <MessageBubble message={message} />
          </div>
        ))}

        {/* Current Question */}
        {currentQuestion && (
          <QuestionCard
            question={currentQuestion}
            isLoading={isLoading}
            onSelectOption={handleSelectOption}
            onSendMessage={onSendMessage}
          />
        )}

        {/* Brain Processing Card (replaces loading dots during instantiation) */}
        {instantiationState && onInstantiationContinue && (
          <div className="mt-2">
            <BrainProcessingCard
              status={instantiationState.status}
              kit={instantiationState.kit as BrainProcessingCardProps['kit']}
              startedAt={instantiationState.startedAt}
              errorCode={instantiationState.errorCode}
              onContinue={onInstantiationContinue}
              onRetry={onInstantiationRetry}
            />
          </div>
        )}

        {/* Loading Indicator (for non-instantiation loading states) */}
        {isLoading && !instantiationState && <LoadingIndicator />}

        {/* Actions */}
        {actions.length > 0 && !currentQuestion && !isAnimating && (
          <div className="flex flex-wrap gap-2 px-4 pb-2">
            {actions.map((action) =>
              action.type === 'text_input' ? (
                <TextInputAction key={action.id} action={action} onSubmit={onActionSelect} />
              ) : (
                <Button
                  key={action.id}
                  variant={action.style === 'primary' ? 'default' : 'outline'}
                  onClick={() => onActionSelect?.(action)}
                  className="min-w-[160px] text-sm"
                  size="lg"
                  data-testid={`guide-action-${action.id}`}
                >
                  {action.text}
                </Button>
              )
            )}
          </div>
        )}

        {/* Blended Widget */}
        {blendedWidget && <div className="mt-4">{blendedWidget}</div>}
      </div>

      {/* Scroll-to-bottom pill */}
      {showScrollPill && (
        <button
          type="button"
          onClick={handleScrollPillClick}
          className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs shadow-lg hover:bg-primary/90 transition-all"
          data-testid="guide-scroll-pill"
        >
          <ChevronDown className="w-3.5 h-3.5" />
          {t('copilot.guide.newMessages')}
        </button>
      )}

      {/* Input */}
      {!currentQuestion && !hideInput && (
        <ChatInput onSendMessage={onSendMessage} isLoading={isLoading} />
      )}
    </div>
  );
}

function TextInputAction({
  action,
  onSubmit,
}: {
  action: IAction;
  onSubmit?: (action: IAction) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (!value.trim()) return;
    onSubmit?.({ ...action, text: value.trim() });
  };

  return (
    <div className="flex gap-2 w-full">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={action.placeholder ?? t('copilot.guide.typeHere')}
        aria-label={t('copilot.aria.messageInput')}
        className="flex-1 text-sm"
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
      />
      <Button
        size="sm"
        onClick={handleSubmit}
        disabled={!value.trim()}
        className="shrink-0"
        aria-label={t('copilot.aria.sendMessage')}
      >
        <Send className="w-4 h-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
